import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, getFormValues, reduxForm } from 'redux-form';

import { CertificationStatus } from '@acceligentllc/shared/enums/certificationStatus';

import type { RootState } from 'af-reducers';

import * as FORMS from 'af-constants/reduxForms';

import { Button } from '@acceligentllc/storybook';

import Input from 'af-fields/Input';
import RadioGroup from 'af-fields/RadioGroup';

import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import styles from './styles.module.scss';

export interface CertificationStatusForm {
	certificationStatus: CertificationStatus;
	reason: Nullable<string>;
}

interface OwnProps {
	showModal: boolean;
	closeModal: () => void;
	openCompanyPolicyModal: () => void;
	onSubmit: (form: CertificationStatusForm) => void;
}

type Props = OwnProps & InjectedFormProps<CertificationStatusForm> & ConnectedProps<typeof connector>;

const CertificationStatusModal: React.FC<Props> = (props: Props) => {
	const {
		closeModal,
		handleSubmit,
		showModal,
		certStatus,
		certReason,
		change,
		reset,
		openCompanyPolicyModal,
		onSubmit,
	} = props;

	const certStatusOptions = React.useMemo(() => Object.keys(CertificationStatus).map((_certStatus) => {
		switch (_certStatus) {
			case (CertificationStatus.OPT_IN): {
				return {
					value: _certStatus,
					label: _certStatus,
					renderLabel: <span>
						I certify that I received and took my meal and rest breaks in accordance with&nbsp;
						<a className={styles.link} onClick={openCompanyPolicyModal}>Company&nbsp;and&nbsp;NPS&nbsp;policies</a>.
					</span>,
				};
			}
			case (CertificationStatus.OPT_OUT): {
				return {
					value: _certStatus,
					label: _certStatus,
					renderLabel: <span>
						I certify that I received but chose not to take my meal and/or rest breaks in accordance with&nbsp;
						<a className={styles.link} onClick={openCompanyPolicyModal}>Company&nbsp;and&nbsp;NPS&nbsp;policies</a>.&nbsp;
						I understand that NPS policy requires compliance with
						California law, and that I am entitled to take all provided breaks.
					</span>,
				};
			}
			case (CertificationStatus.OPT_OUT_WITH_REASON): {
				return {
					value: _certStatus,
					label: _certStatus,
					renderLabel: <span>
						I certify that I did not receive and was unable to take my meal and/or rest breaks in accordance with&nbsp;
						<a className={styles.link} onClick={openCompanyPolicyModal}>Company&nbsp;and&nbsp;NPS&nbsp;policies</a> because&nbsp;of:
					</span>,
				};
			}
			default: {
				return {
					value: _certStatus,
					label: _certStatus,
					renderLabel: <span>
						I certify that I received and took my meal and rest breaks in accordance with&nbsp;
						<a className={styles.link} onClick={openCompanyPolicyModal}>Company policy</a>.
					</span>,
				};
			}
		}
	}), [openCompanyPolicyModal]);

	const onCertStatusChange = React.useCallback((field: string, value: CertificationStatus) => {
		if (value !== CertificationStatus.OPT_OUT_WITH_REASON) {
			change('reason', null);
		}
		change(field, value);
	}, [change]);

	const onCloseModal = React.useCallback(() => {
		reset();
		closeModal();
	}, [reset, closeModal]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="md"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title="Confirm Certification Status"
			/>
			<CustomModal.Body>
				<div className={styles['modal-header']}>
					Before signing your time card, please confirm your Certification status for this shift.
				</div>
				<RadioGroup
					changeField={onCertStatusChange}
					field={'certificationStatus'}
					items={certStatusOptions}
					radioButtonClass={styles['radio-option']}
				/>
				{certStatus === CertificationStatus.OPT_OUT_WITH_REASON && (
					<Field
						component={Input}
						label="Reason for no break"
						name="reason"
						placeholder="Enter reason for no break"
						type="text"
					/>
				)}
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onCloseModal}
					style="secondary"
				/>
				<SubmitButton
					disabled={!certStatus || (certStatus === CertificationStatus.OPT_OUT_WITH_REASON && !certReason)}
					label="Confirm Certification Status"
					onClick={handleSubmit(onSubmit)}
					variant="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

const mapStateToProps = (state: RootState) => ({
	certStatus: (getFormValues(FORMS.CERTIFICATION_STATUS_FORM)(state) as CertificationStatusForm)?.certificationStatus,
	certReason: (getFormValues(FORMS.CERTIFICATION_STATUS_FORM)(state) as CertificationStatusForm)?.reason,
});

const connector = connect(mapStateToProps);

const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	reduxForm<CertificationStatusForm>({
		form: FORMS.CERTIFICATION_STATUS_FORM,
	})
);

export default enhance(CertificationStatusModal);
